/** Expandable per-retailer discovery config for multi-select Auto Sourcing. */
import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import RetailerLogo from "./RetailerLogo";
import { canSelectSource } from "./RetailerPicker";
import CategoryPicker, { isSelectionReady } from "./CategoryPicker";
import ManualCategoryUrls, { manualUrlsReady, parseCategoryUrls } from "./ManualCategoryUrls";
import { listSourceCategories, listManualCategories } from "@/lib/autoSourcing/api";
import "./AutoSourcing.css";

function emptySlice() {
  return {
    // Default to All so multi-select starts runnable; users refine per retailer.
    category_selection: { mode: "all" },
    selected_categories: [],
    custom_category_query: null,
    category_urls: [],
    max_products: null,
  };
}

function sliceSummary(slice, source) {
  const sel = slice?.category_selection;
  const cats = Array.isArray(slice?.selected_categories) ? slice.selected_categories : [];
  if (sel?.mode === "custom" && sel.user_query) return `Custom: ${sel.user_query}`;
  if (sel?.mode === "urls") {
    const n = (slice?.category_urls || []).length;
    return n ? `${n} category URL${n === 1 ? "" : "s"}` : "Category URLs";
  }
  if (cats.length > 1) return cats.map((c) => c.name || c.category_id).join(", ");
  if (cats.length === 1 || sel?.mode === "category") {
    return cats[0]?.name || sel?.name || "1 category";
  }
  if (sel?.mode === "all") return "All categories";
  const mode = source?.category_support?.mode;
  if (mode === "search_query") return "Choose a search";
  return "Choose categories";
}

function isSliceReady(slice, source, { categoriesLoaded, categoryAvailable }) {
  const sel = slice?.category_selection;
  const cats = Array.isArray(slice?.selected_categories) ? slice.selected_categories : [];
  if (!source || !canSelectSource(source)) return false;
  const cs = source.category_support;
  const discovery = source.category_discovery_mode;
  if (discovery === "pending") return false;
  if (discovery === "manual" && !(cs?.supported && (cs?.mode === "search_query" || cs?.mode === "category_catalog"))) {
    return manualUrlsReady(slice?.category_urls || [], source.domain);
  }
  if (sel?.mode === "urls") return manualUrlsReady(slice?.category_urls || [], source.domain);
  if (cats.length > 0) return !categoriesLoaded || categoryAvailable !== false;
  return isSelectionReady(sel, { categoryAvailable: categoryAvailable !== false });
}

export function allRetailerScopesReady(sourceIds, sourceScope, sourcesById, metaById) {
  if (!sourceIds?.length) return false;
  return sourceIds.every((sid) => {
    const source = sourcesById.get(sid);
    const slice = sourceScope?.[sid] || emptySlice();
    const meta = metaById?.[sid] || {};
    return isSliceReady(slice, source, meta);
  });
}

export default function RetailerScopePanels({
  sourceIds = [],
  sources = [],
  sourceScope = {},
  onChangeSlice,
}) {
  const [openId, setOpenId] = useState(sourceIds[0] || null);
  const [catalogById, setCatalogById] = useState({});
  const [loadedById, setLoadedById] = useState({});
  const [manualById, setManualById] = useState({});

  const sourcesById = useMemo(() => {
    const m = new Map();
    sources.forEach((s) => m.set(s.source_id, s));
    return m;
  }, [sources]);

  useEffect(() => {
    if (sourceIds.length && !sourceIds.includes(openId)) {
      setOpenId(sourceIds[0]);
    }
  }, [sourceIds, openId]);

  useEffect(() => {
    let cancelled = false;
    sourceIds.forEach((sid) => {
      const source = sourcesById.get(sid);
      if (!source) return;
      const mode = source.category_support?.mode;
      if (mode === "category_catalog" && loadedById[sid] == null && typeof listSourceCategories === "function") {
        Promise.resolve(listSourceCategories(sid))
          .then((data) => {
            if (cancelled) return;
            setCatalogById((prev) => ({ ...prev, [sid]: Array.isArray(data?.items) ? data.items : [] }));
            setLoadedById((prev) => ({ ...prev, [sid]: true }));
          })
          .catch(() => {
            if (cancelled) return;
            setCatalogById((prev) => ({ ...prev, [sid]: [] }));
            setLoadedById((prev) => ({ ...prev, [sid]: true }));
          });
      }
      if (source.category_discovery_mode === "manual" && typeof listManualCategories === "function") {
        Promise.resolve(listManualCategories(sid))
          .then((data) => {
            if (!cancelled) setManualById((prev) => ({ ...prev, [sid]: Array.isArray(data?.items) ? data.items : [] }));
          })
          .catch(() => {});
      }
    });
    return () => {
      cancelled = true;
    };
  }, [sourceIds, sourcesById, loadedById]);

  if (!sourceIds.length) return null;

  return (
    <div className="as-retailer-scope-panels" data-testid="retailer-scope-panels">
      <div className="as-section-title" style={{ marginBottom: 8 }}>
        Per-retailer search
      </div>
      <div className="as-section-description" style={{ marginBottom: 12 }}>
        Configure categories or keywords independently for each selected retailer.
      </div>
      {sourceIds.map((sid) => {
        const source = sourcesById.get(sid);
        if (!source) return null;
        const slice = sourceScope[sid] || emptySlice();
        const open = openId === sid;
        const categories = catalogById[sid] || [];
        const loaded = Boolean(loadedById[sid]);
        const cs = source.category_support;
        const discovery = source.category_discovery_mode;
        const catalogMode = cs?.supported && cs?.mode === "category_catalog";
        const searchMode = cs?.supported && (cs?.mode === "search_query" || cs?.mode === "category_catalog");
        const selectedCats = Array.isArray(slice.selected_categories) ? slice.selected_categories : [];
        const selectedIds = new Set(selectedCats.map((c) => c.category_id));

        const patch = (next) => onChangeSlice(sid, { ...emptySlice(), ...slice, ...next });

        const setSelection = (selection) => {
          if (!selection) {
            patch({
              category_selection: null,
              selected_categories: [],
              custom_category_query: null,
              category_urls: [],
            });
            return;
          }
          if (selection.mode === "all") {
            patch({
              category_selection: { mode: "all" },
              selected_categories: [],
              custom_category_query: null,
              category_urls: [],
            });
            return;
          }
          if (selection.mode === "custom") {
            const q = selection.user_query || "";
            patch({
              category_selection: { mode: "custom", user_query: q, resolver: "raw_query" },
              selected_categories: [],
              custom_category_query: q.trim() ? q : null,
              category_urls: [],
            });
            return;
          }
          if (selection.mode === "category") {
            const cat = categories.find((c) => c.category_id === selection.category_id) || selection;
            const entry = {
              category_id: cat.category_id,
              name: cat.name,
              url: cat.url,
            };
            patch({
              category_selection: {
                mode: "category",
                category_id: entry.category_id,
                name: entry.name,
                url: entry.url,
              },
              selected_categories: [entry],
              custom_category_query: null,
              category_urls: [],
            });
          }
        };

        const toggleCategory = (cat) => {
          const exists = selectedIds.has(cat.category_id);
          const next = exists
            ? selectedCats.filter((c) => c.category_id !== cat.category_id)
            : [...selectedCats, { category_id: cat.category_id, name: cat.name, url: cat.url }];
          if (!next.length) {
            patch({
              category_selection: null,
              selected_categories: [],
              custom_category_query: null,
              category_urls: [],
            });
            return;
          }
          const primary = next[0];
          patch({
            category_selection: {
              mode: "category",
              category_id: primary.category_id,
              name: primary.name,
              url: primary.url,
            },
            selected_categories: next,
            custom_category_query: null,
            category_urls: [],
          });
        };

        const setUrlText = (text) => {
          const urls = parseCategoryUrls(text);
          if (!urls.length) {
            patch({
              category_selection: slice.category_selection?.mode === "urls" ? null : slice.category_selection,
              category_urls: [],
            });
            return;
          }
          patch({
            category_selection: { mode: "urls" },
            category_urls: urls,
            selected_categories: [],
            custom_category_query: null,
          });
        };

        return (
          <div
            key={sid}
            className={`as-retailer-scope-panel${open ? " is-open" : ""}`}
            data-testid={`retailer-scope-panel-${sid}`}
          >
            <button
              type="button"
              className="as-retailer-scope-toggle"
              aria-expanded={open}
              onClick={() => setOpenId(open ? null : sid)}
              data-testid={`retailer-scope-toggle-${sid}`}
            >
              <RetailerLogo source={source} size="sm" />
              <span className="as-retailer-scope-toggle-copy">
                <strong>{source.name || source.domain}</strong>
                <small>{sliceSummary(slice, source)}</small>
              </span>
              <ChevronDown className="as-retailer-scope-chevron" aria-hidden="true" />
            </button>
            {open ? (
              <div className="as-retailer-scope-body">
                {discovery === "pending" ? (
                  <p className="as-helper">Search options for this retailer are still being prepared.</p>
                ) : null}

                {(catalogMode || searchMode) && discovery !== "pending" ? (
                  <div className="as-field" style={{ marginBottom: 12 }}>
                    <div className="as-label">Category / search</div>
                    <CategoryPicker
                      categories={categories}
                      value={
                        selectedCats.length > 1
                          ? null
                          : slice.category_selection?.mode === "category" && selectedCats[0]
                            ? {
                                mode: "category",
                                category_id: selectedCats[0].category_id,
                                name: selectedCats[0].name,
                                url: selectedCats[0].url,
                              }
                            : slice.category_selection
                      }
                      onChange={setSelection}
                      disabled={!canSelectSource(source)}
                      loading={catalogMode && !loaded}
                      placeholder={selectedCats.length > 1 ? `${selectedCats.length} categories` : "Select category"}
                    />
                  </div>
                ) : null}

                {catalogMode && loaded && categories.length > 0 ? (
                  <div className="as-field" style={{ marginBottom: 12 }} data-testid={`retailer-multi-cats-${sid}`}>
                    <div className="as-label">Add more categories (optional)</div>
                    <div className="as-retailer-multi-cats">
                      {categories
                        .filter((c) => c.selectable !== false && c.url)
                        .slice(0, 40)
                        .map((cat) => {
                          const on = selectedIds.has(cat.category_id);
                          return (
                            <button
                              key={cat.category_id}
                              type="button"
                              className={`as-retailer-cat-chip${on ? " is-on" : ""}`}
                              aria-pressed={on}
                              onClick={() => toggleCategory(cat)}
                            >
                              {cat.name}
                            </button>
                          );
                        })}
                    </div>
                  </div>
                ) : null}

                {(discovery === "manual" || slice.category_selection?.mode === "urls") && (
                  <div className="as-field" style={{ marginBottom: 12 }}>
                    <div className="as-label">Category URL(s)</div>
                    <ManualCategoryUrls
                      value={(slice.category_urls || []).join("\n")}
                      onChange={setUrlText}
                      domain={source.domain}
                      saved={manualById[sid] || []}
                      optional={discovery !== "manual"}
                    />
                  </div>
                )}

                <div className="as-field">
                  <div className="as-label">Products for this retailer (optional)</div>
                  <input
                    className="as-input"
                    type="number"
                    min={1}
                    max={500}
                    placeholder="Use shared scan limit"
                    value={slice.max_products ?? ""}
                    onChange={(e) => {
                      const v = e.target.value.trim();
                      patch({ max_products: v ? Number(v) : null });
                    }}
                    data-testid={`retailer-max-products-${sid}`}
                  />
                </div>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export { emptySlice, sliceSummary, isSliceReady };
